import { useState, useEffect, useRef } from 'react';
import { apiFetch } from '../lib/api';

// Sprint BRAND BRAND.A2 (#835) — per-project branding override card.
//
// Mirrors EscalationOverridesSidebarCard: collapsible, CSS-var inline styles,
// fetch-on-open, PATCH-on-save. Lets a TENANT_ADMIN / PRODUCT_MANAGER override
// the tenant branding (BRAND.A1) for this one project. Each editable field
// shows the inherited value below it as a grayed reference ("Tenant default:
// …" when the tenant set it, "Default: …" when it falls through to the
// platform default) so the admin always sees what an empty field inherits.
//
// REPLACE semantics (matches the PATCH endpoint): the saved object is the
// stored override; clearing every field stores NULL and the project re-inherits
// the tenant branding. Logo override is out of scope until BRAND.C1 (#839).

const HEX_COLOUR = /^#[0-9A-Fa-f]{6}$/;

type Field = 'displayName' | 'primaryColor' | 'secondaryColor';

interface BrandingOverride {
  displayName?:    string;
  primaryColor?:   string;
  secondaryColor?: string;
}

interface GetResponse {
  override: BrandingOverride | null;
  tenant: {
    displayName:    string | null;
    primaryColor:   string | null;
    secondaryColor: string | null;
  };
  defaults: {
    primaryColor:   string;
    secondaryColor: string;
  };
}

interface Props {
  projectId: string;
}

const FIELDS: Array<{ field: Field; label: string; kind: 'text' | 'colour' }> = [
  { field: 'displayName',    label: 'Display name',    kind: 'text' },
  { field: 'primaryColor',   label: 'Primary colour',  kind: 'colour' },
  { field: 'secondaryColor', label: 'Secondary colour', kind: 'colour' },
];

const cardCls = 'rounded-xl border shadow-sm transition-shadow duration-150';
const cardStyle: React.CSSProperties = {
  background: 'var(--surface)', borderColor: 'var(--border)', padding: 'var(--space-5)',
};

export default function ProjectBrandingOverrideSidebarCard({ projectId }: Props) {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState<GetResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState<number | null>(null);
  const [values, setValues] = useState<Record<Field, string>>({
    displayName: '', primaryColor: '', secondaryColor: '',
  });
  const cancelledRef = useRef(false);

  useEffect(() => {
    cancelledRef.current = false;
    if (!open || !projectId) return;
    setLoading(true);
    setError('');
    apiFetch<GetResponse>(`/projects/${projectId}/branding-override`)
      .then(r => {
        if (cancelledRef.current) return;
        setData(r);
        setValues({
          displayName:    r.override?.displayName    ?? '',
          primaryColor:   r.override?.primaryColor   ?? '',
          secondaryColor: r.override?.secondaryColor ?? '',
        });
      })
      .catch(e => { if (!cancelledRef.current) setError(e instanceof Error ? e.message : 'Failed to load'); })
      .finally(() => { if (!cancelledRef.current) setLoading(false); });
    return () => { cancelledRef.current = true; };
  }, [open, projectId]);

  // Inherited reference shown grayed under each field. Tenant-set value wins
  // the "Tenant default" label; otherwise the platform default is shown.
  const inheritedHint = (field: Field): string => {
    if (!data) return '';
    if (field === 'displayName') {
      return data.tenant.displayName
        ? `Tenant default: ${data.tenant.displayName}`
        : 'Default: BluePrint';
    }
    const tenantVal = field === 'primaryColor' ? data.tenant.primaryColor : data.tenant.secondaryColor;
    const defaultVal = field === 'primaryColor' ? data.defaults.primaryColor : data.defaults.secondaryColor;
    return tenantVal ? `Tenant default: ${tenantVal}` : `Default: ${defaultVal}`;
  };

  const setValue = (field: Field, raw: string) => {
    setValues(prev => ({ ...prev, [field]: raw }));
    setSavedAt(null);
    setError('');
  };

  const handleSave = async () => {
    setError('');

    const override: BrandingOverride = {};
    const displayName = values.displayName.trim();
    const primary = values.primaryColor.trim();
    const secondary = values.secondaryColor.trim();

    if (displayName) override.displayName = displayName;
    if (primary) {
      if (!HEX_COLOUR.test(primary)) { setError('Primary colour must be a 6-digit hex like #7C3AED'); return; }
      override.primaryColor = primary;
    }
    if (secondary) {
      if (!HEX_COLOUR.test(secondary)) { setError('Secondary colour must be a 6-digit hex like #EC4899'); return; }
      override.secondaryColor = secondary;
    }
    // #1434: the client-settable logoS3Key/logoMimeType surface was deleted, so
    // there is nothing to preserve here. A colour-only save sends only the
    // colour/name fields; any legacy logo key on the stored row is dropped on the
    // next save (and stripped server-side by migration 0160).

    setSaving(true);
    try {
      await apiFetch(`/projects/${projectId}/branding-override`, {
        method: 'PATCH',
        body: JSON.stringify(override),
      });
      const refreshed = await apiFetch<GetResponse>(`/projects/${projectId}/branding-override`);
      if (!cancelledRef.current) {
        setData(refreshed);
        setValues({
          displayName:    refreshed.override?.displayName    ?? '',
          primaryColor:   refreshed.override?.primaryColor   ?? '',
          secondaryColor: refreshed.override?.secondaryColor ?? '',
        });
        setSavedAt(Date.now());
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={cardCls} style={cardStyle}>
      <button
        type="button"
        onClick={() => setOpen(v => !v)}
        data-testid="project-branding-toggle"
        aria-expanded={open}
        style={{
          width: '100%', background: 'none', border: 'none', padding: 0, cursor: 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}
      >
        <p
          className="uppercase tracking-wider"
          style={{
            fontFamily: 'var(--font-body)', fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)',
            margin: 0, letterSpacing: '0.05em',
            borderLeft: '3px solid var(--primary)', paddingLeft: 8,
          }}
        >
          Project Branding (override tenant)
        </p>
        <span style={{ color: 'var(--text-secondary)', fontSize: 12 }}>{open ? '▾' : '▸'}</span>
      </button>

      {open && (
        <div style={{ marginTop: 12 }}>
          {loading && (
            <p style={{ fontSize: 13, color: 'var(--text-secondary)' }}>Loading…</p>
          )}
          {error && (
            <div data-testid="project-branding-error" style={{
              padding: '6px 10px', background: 'rgba(239,68,68,0.08)', color: 'var(--error)',
              borderRadius: 6, marginBottom: 10, fontSize: 12,
            }}>{error}</div>
          )}
          {savedAt && !error && (
            <div data-testid="project-branding-saved" style={{
              padding: '6px 10px', background: 'rgba(16,185,129,0.08)', color: '#10b981',
              borderRadius: 6, marginBottom: 10, fontSize: 12,
            }}>Saved</div>
          )}

          {!loading && data && FIELDS.map(({ field, label, kind }) => (
            <div key={field} style={{ marginBottom: 12 }}>
              <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text)', display: 'block', marginBottom: 4 }}>
                {label}
              </label>
              <input
                type="text"
                value={values[field]}
                onChange={e => setValue(field, e.target.value)}
                disabled={saving}
                placeholder={kind === 'colour' ? '#RRGGBB' : 'Inherit from tenant'}
                data-testid={`project-branding-input-${field}`}
                style={{
                  width: '100%', padding: '4px 8px', fontSize: 12,
                  border: '1px solid var(--border)', borderRadius: 4,
                  background: 'var(--bg)', color: 'var(--text)',
                }}
              />
              <span
                data-testid={`project-branding-hint-${field}`}
                style={{ fontSize: 11, color: 'var(--text-secondary)', display: 'block', marginTop: 2 }}
              >
                {inheritedHint(field)}
              </span>
            </div>
          ))}

          {!loading && data && (
            <button
              type="button"
              onClick={() => void handleSave()}
              disabled={saving}
              data-testid="project-branding-save"
              style={{
                marginTop: 4, padding: '6px 14px', fontSize: 12, fontWeight: 600,
                border: 'none', borderRadius: 6, cursor: saving ? 'not-allowed' : 'pointer',
                background: 'var(--primary)', color: '#fff', opacity: saving ? 0.6 : 1,
              }}
            >
              {saving ? 'Saving…' : 'Save'}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
